import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import './AboutTab.css';
import Toast from '../../Academy/Academylms/common/Toast';

const genderOptions = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' }
];

const languageOptions = [
  { value: 'english', label: 'English' },
  { value: 'spanish', label: 'Spanish' },
  { value: 'french', label: 'French' },
  { value: 'german', label: 'German' },
  { value: 'italian', label: 'Italian' },
  { value: 'portuguese', label: 'Portuguese' },
  { value: 'russian', label: 'Russian' },
  { value: 'japanese', label: 'Japanese' },
  { value: 'chinese', label: 'Chinese' },
  { value: 'korean', label: 'Korean' },
  { value: 'arabic', label: 'Arabic' },
  { value: 'hindi', label: 'Hindi' },
  // Add more languages as needed
];

const cefrLevels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'Not sure'];

const AboutTab = ({
  profile,
  isEditingBio,
  setIsEditingBio,
  editedBio,
  setEditedBio,
  setProfile,
  user,
  supabase,
  nativeLanguages,
  targetLanguages,
  isOwnProfile
}) => {
  // Learning languages edit state
  const [isEditingLearning, setIsEditingLearning] = useState(false);
  const [editedLearning, setEditedLearning] = useState(
    targetLanguages
      ? targetLanguages.map(lang =>
          typeof lang === 'object' && lang.value
            ? languageOptions.find(opt => opt.value === lang.value) || { value: lang.value, label: lang.value }
            : languageOptions.find(opt => opt.value === lang) || { value: lang, label: lang }
        )
      : []
  );
  const [editedLevels, setEditedLevels] = useState(profile.target_language_levels || {});

  // Personal info edit state
  const [isEditingPersonal, setIsEditingPersonal] = useState(false);
  const [editedPersonal, setEditedPersonal] = useState({
    date_of_birth: profile.date_of_birth || '',
    gender: profile.gender || '',
    location: profile.location || ''
  });

  // Privacy (wall visibility) state
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [privacySaved, setPrivacySaved] = useState(false);
  const [wallPrivacy, setWallPrivacy] = useState(profile.wall_privacy || 'public');
  const [toast, setToast] = useState({ open: false, message: '', type: 'success', duration: 2500 });
  const showToast = (message, type = 'success', duration = 2500) => setToast({ open: true, message, type, duration });
  const closeToast = () => setToast(t => ({ ...t, open: false }));

  // Keep wallPrivacy in sync if profile prop updates
  useEffect(() => {
    if (profile?.wall_privacy) setWallPrivacy(profile.wall_privacy);
  }, [profile?.wall_privacy]);

  const handlePersonalChange = (e) => {
    const { name, value } = e.target;
    setEditedPersonal(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSavePrivacy = async (e) => {
    e?.preventDefault?.();
    if (!isOwnProfile) return;
    try {
      setSavingPrivacy(true);
      const { error } = await supabase
        .from('profiles')
        .update({ wall_privacy: wallPrivacy })
        .eq('id', user.id);
      if (!error) {
        setProfile(prev => ({ ...prev, wall_privacy: wallPrivacy }));
        setPrivacySaved(true);
        setTimeout(() => setPrivacySaved(false), 2000);
        showToast('Privacy saved', 'success');
      }
    } catch (e) {
      showToast('Failed to save privacy', 'error');
    } finally {
      setSavingPrivacy(false);
    }
  };

  const handleGenderChange = (selected) => {
    setEditedPersonal(prev => ({
      ...prev,
      gender: selected ? selected.value : ''
    }));
  };

  const handleSavePersonal = async () => {
    if (
      editedPersonal.date_of_birth === profile.date_of_birth &&
      editedPersonal.gender === profile.gender &&
      editedPersonal.location === profile.location
    ) {
      showToast('No changes made', 'info');
      return;
    }

    await supabase
      .from('profiles')
      .update({
        date_of_birth: editedPersonal.date_of_birth || null,
        gender: editedPersonal.gender,
        location: editedPersonal.location
      })
      .eq('id', user.id);

    setProfile(prev => ({
      ...prev,
      date_of_birth: editedPersonal.date_of_birth || null,
      gender: editedPersonal.gender,
      location: editedPersonal.location
    }));
    setIsEditingPersonal(false);
    showToast('Personal info saved', 'success');
  };

  const computeAge = (dobStr) => {
    if (!dobStr) return '';
    const dob = new Date(dobStr);
    if (isNaN(dob.getTime())) return '';
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    return age >= 0 ? age : '';
  };

  const handleSaveLearning = async () => {
    const updatedLearning = editedLearning.map(l => l.value);
    
    // Validate that all selected languages have levels
    const missingLevels = updatedLearning.filter(lang => !editedLevels[lang] || editedLevels[lang].trim() === '');
    if (missingLevels.length > 0) {
      showToast('Please select a level for all languages', 'error');
      return;
    }

    // Clean up levels object to only include selected languages
    const cleanedLevels = {};
    updatedLearning.forEach(lang => {
      cleanedLevels[lang] = editedLevels[lang];
    });

    await supabase
      .from('profiles')
      .update({ 
        target_languages: updatedLearning,
        target_language_levels: cleanedLevels
      })
      .eq('id', user.id);

    setProfile(prev => ({
      ...prev,
      target_languages: updatedLearning,
      target_language_levels: cleanedLevels
    }));
    setIsEditingLearning(false);
    showToast('Learning languages and levels saved', 'success');
  };

  const handleLevelChange = (language, level) => {
    setEditedLevels(prev => ({
      ...prev,
      [language]: level
    }));
  };

  return (
    <div className="profile-section">
      <Toast open={toast.open} message={toast.message} type={toast.type} duration={toast.duration} onClose={closeToast} />
      <h2>About Me</h2>
      {isOwnProfile && isEditingBio ? (
        <div>
          <textarea
            className="bio-textarea"
            value={editedBio}
            onChange={e => setEditedBio(e.target.value)}
            placeholder="Write something about yourself..."
          />
          <button
            className="edit-btn"
            onClick={async () => {
              await supabase
                .from('profiles')
                .update({ bio: editedBio })
                .eq('id', user.id);
              setProfile(prev => ({ ...prev, bio: editedBio }));
              setIsEditingBio(false);
            }}
          >
            Save
          </button>
          <button
            className="edit-btn"
            style={{ marginLeft: 8, background: 'var(--primary-light)' }}
            onClick={() => {
              setEditedBio(profile.bio || '');
              setIsEditingBio(false);
            }}
          >
            Cancel
          </button>
        </div>
      ) : (
        <div style={{ marginBottom: 16 }}>
          <p>{profile.bio || 'No bio provided yet.'}</p>
          {isOwnProfile && (
            <button
              className="edit-btn"
              onClick={() => setIsEditingBio(true)}
            >
              Edit Bio
            </button>
          )}
        </div>
      )}

      {/* Personal Info Section */}
      {isEditingPersonal && isOwnProfile ? (
        <div className="profile-details">
          <div className="profile-detail-item">
            <span className="detail-label">Date of Birth:</span>
            <input
              type="date"
              name="date_of_birth"
              value={editedPersonal.date_of_birth || ''}
              onChange={handlePersonalChange}
              className="edit-input"
            />
            <span className="detail-hint" style={{ marginLeft: 8, color: 'var(--text-secondary)' }}>
              {computeAge(editedPersonal.date_of_birth) !== '' ? `Age: ${computeAge(editedPersonal.date_of_birth)}` : ''}
            </span>
          </div>
          <div className="profile-detail-item">
            <span className="detail-label">Gender:</span>
            <Select
              name="gender"
              options={genderOptions}
              value={genderOptions.find(opt => opt.value === editedPersonal.gender) || null}
              onChange={handleGenderChange}
              className="react-select-container"
              classNamePrefix="react-select"
              isClearable
              placeholder="Select gender"
            />
          </div>
          <div className="profile-detail-item">
            <span className="detail-label">Location:</span>
            <input
              type="text"
              name="location"
              value={editedPersonal.location}
              onChange={handlePersonalChange}
              className="edit-input"
            />
          </div>
          <button className="edit-btn" onClick={handleSavePersonal}>Save</button>
          <button
            className="edit-btn"
            style={{ marginLeft: 8, background: 'var(--primary-light)' }}
            onClick={() => {
              setEditedPersonal({
                date_of_birth: profile.date_of_birth || '',
                gender: profile.gender || '',
                location: profile.location || ''
              });
              setIsEditingPersonal(false);
            }}
          >
            Cancel
          </button>
        </div>
      ) : (
        (profile.date_of_birth || profile.age || profile.gender || profile.location) && (
          <div className="profile-details">
            {(profile.date_of_birth || profile.age) && (
              <div className="profile-detail-item">
                <span className="detail-label">Age:</span>
                <span className="detail-value">{profile.date_of_birth ? computeAge(profile.date_of_birth) : profile.age}</span>
              </div>
            )}
            {profile.gender && (
              <div className="profile-detail-item">
                <span className="detail-label">Gender:</span>
                <span className="detail-value">{profile.gender}</span>
              </div>
            )}
            {profile.location && (
              <div className="profile-detail-item">
                <span className="detail-label">Location:</span>
                <span className="detail-value">{profile.location}</span>
              </div>
            )}
            {isOwnProfile && (
              <button
                className="edit-btn"
                style={{ marginTop: 10 }}
                onClick={() => setIsEditingPersonal(true)}
              >
                Edit Personal Info
              </button>
            )}
          </div>
        )
      )}

      <h2 style={{ marginTop: '2rem' }}>Languages</h2>
      <div className="languages-container">
        <div className="language-group">
          <h3>Native Languages</h3>
          {nativeLanguages && nativeLanguages.length > 0 ? (
            <ul className="language-list">
              {nativeLanguages.map((lang, index) => (
                <li key={index} className="language-tag">{lang}</li>
              ))}
            </ul>
          ) : (
            <p>No native languages specified.</p>
          )}
        </div>
        <div className="language-group">
          <h3>Learning</h3>
          {isOwnProfile && isEditingLearning ? (
            <div>
              <Select
                isMulti
                name="targetLanguages"
                options={languageOptions}
                className="react-select-container"
                classNamePrefix="react-select"
                value={editedLearning}
                onChange={(selected) => {
                  setEditedLearning(selected);
                  // Initialize levels for new languages
                  const newLangs = (selected || []).map(opt => opt.value);
                  const currentLevels = { ...editedLevels };
                  newLangs.forEach(lang => {
                    if (!currentLevels[lang]) currentLevels[lang] = '';
                  });
                  setEditedLevels(currentLevels);
                }}
                placeholder="Select languages you want to learn"
              />
              {editedLearning.length > 0 && (
                <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <strong>Your Level in Each Language:</strong>
                  {editedLearning.map((langOpt) => {
                    const lang = langOpt.value;
                    const label = langOpt.label;
                    return (
                      <div key={lang} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ minWidth: '120px', fontWeight: '500' }}>{label}:</span>
                        <select
                          value={editedLevels[lang] || ''}
                          onChange={(e) => handleLevelChange(lang, e.target.value)}
                          style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                        >
                          <option value="">Select level</option>
                          {cefrLevels.map(lvl => (
                            <option key={lvl} value={lvl}>{lvl}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{ marginTop: '12px' }}>
                <button className="edit-btn" onClick={handleSaveLearning}>Save</button>
                <button
                  className="edit-btn"
                  style={{ marginLeft: 8, background: 'var(--primary-light)' }}
                  onClick={() => {
                    setEditedLearning(
                      targetLanguages
                        ? targetLanguages.map(lang =>
                            typeof lang === 'object' && lang.value
                              ? languageOptions.find(opt => opt.value === lang.value) || { value: lang.value, label: lang.value }
                              : languageOptions.find(opt => opt.value === lang) || { value: lang, label: lang }
                          )
                        : []
                    );
                    setEditedLevels(profile.target_language_levels || {});
                    setIsEditingLearning(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              {targetLanguages && targetLanguages.length > 0 ? (
                <div>
                  <ul className="language-list">
                    {targetLanguages.map((lang, index) => {
                      const langValue = typeof lang === 'object' ? lang.value : lang;
                      const langLabel = typeof lang === 'object' && lang.label ? lang.label : lang;
                      const level = (profile.target_language_levels || {})[langValue];
                      return (
                        <li key={index} className="language-tag">
                          {langLabel} {level ? `(${level})` : ''}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : (
                <p>No learning languages specified.</p>
              )}
              {isOwnProfile && (
                <button
                  className="edit-btn"
                  onClick={() => setIsEditingLearning(true)}
                >
                  Edit Learning Languages
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {isOwnProfile && (
        <div className="profile-section" style={{ marginTop: '2rem' }}>
          <h2>Privacy</h2>
          <form onSubmit={handleSavePrivacy}>
            <div className="profile-details">
              <div className="profile-detail-item" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <span className="detail-label">Wall visibility</span>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="wall_privacy"
                      value="public"
                      checked={wallPrivacy === 'public'}
                      onChange={() => setWallPrivacy('public')}
                    />
                    Public (any logged-in user can view and post as allowed)
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="wall_privacy"
                      value="connections"
                      checked={wallPrivacy === 'connections'}
                      onChange={() => setWallPrivacy('connections')}
                    />
                    Connections only
                  </label>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  type="submit"
                  className="edit-btn"
                  disabled={savingPrivacy || (wallPrivacy === (profile.wall_privacy || 'public'))}
                >
                  {savingPrivacy ? 'Saving…' : 'Save Privacy'}
                </button>
                {privacySaved && (
                  <span style={{ color: 'var(--primary, #6e8efb)' }}>Saved</span>
                )}
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AboutTab;